// src/engine/stroke.js
//
// Freehand stroke engine: "rope" stabilizer (same algorithm as Tattune's
// GuidePaint) + smooth, pressure-aware stroke rendering.
//
// Strokes are stored as point lists so undo can simply re-render, instead
// of keeping heavy per-stroke canvas snapshots.
//
//   stroke = { color, erase, points: [{ x, y, w }] }   (w = line width px)

/**
 * Lazy-rope stabilizer: the brush trails the pen on an invisible string of
 * length `rope` px and only moves when the string pulls tight, so wobble
 * shorter than the string vanishes. A light moving average on top rounds
 * off the corners. Distance-based, so it behaves the same regardless of
 * how often the tablet/mouse reports (unlike a per-event average).
 */
export class RopeStabilizer {
  constructor({ enabled = true, rope = 12, strength = 0.35, minDist = 0.4 } = {}) {
    this.enabled = enabled;
    this.rope = rope;         // px — string length (bigger = steadier)
    this.strength = strength; // 0..1 extra smoothing after the rope
    this.minDist = minDist;   // px — ignore micro jitter
    this.reset();
  }
  reset() {
    this.brush = null; // rope end
    this.out = null;   // smoothed output
    this.raw = null;   // latest pen position
  }
  push(x, y) {
    if (!this.enabled || !this.brush) {
      this.brush = { x, y };
      this.out = { x, y };
      this.raw = { x, y };
      return { x, y };
    }
    if (Math.hypot(x - this.raw.x, y - this.raw.y) < this.minDist) return { ...this.out };
    this.raw = { x, y };
    const dx = x - this.brush.x, dy = y - this.brush.y;
    const d = Math.hypot(dx, dy);
    if (d > this.rope) {
      const k = (d - this.rope) / d;
      this.brush.x += dx * k;
      this.brush.y += dy * k;
    }
    const s = Math.max(0, Math.min(0.9, this.strength));
    this.out.x += (this.brush.x - this.out.x) * (1 - s);
    this.out.y += (this.brush.y - this.out.y) * (1 - s);
    return { ...this.out };
  }
  /** Points that walk the trailing brush to where the pen lifted, so a
   *  stabilized stroke doesn't stop short. */
  catchUp(steps = 10) {
    if (!this.enabled || !this.out || !this.raw) return [];
    const out = [];
    const sx = this.out.x, sy = this.out.y;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      out.push({ x: sx + (this.raw.x - sx) * t, y: sy + (this.raw.y - sy) * t });
    }
    this.brush = { ...this.raw };
    this.out = { ...this.raw };
    return out;
  }
}

/** Opacity 0..1 from pen pressure (Apple Pencil, Wacom); 1 for mouse/touch. */
export function alphaForPressure(e) {
  if (e?.pointerType === "pen" && typeof e.pressure === "number" && e.pressure > 0) {
    return Math.min(1, 0.15 + e.pressure * 1.1);
  }
  return 1;
}

/** Width from pointer pressure. Pens (Apple Pencil) vary; mouse/touch
 *  report a constant 0.5, so they get the base width. */
export function widthForPressure(base, e) {
  if (e?.pointerType === "pen" && typeof e.pressure === "number" && e.pressure > 0) {
    return base * (0.35 + e.pressure * 1.1);
  }
  return base;
}

/** Hard-edged smoothed path (quadratic curves through point midpoints),
 *  segment-by-segment so width can follow pressure. */
function drawPath(ctx, pts, color) {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  if (pts.length === 1) {
    ctx.beginPath();
    ctx.arc(pts[0].x, pts[0].y, pts[0].w / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  let prevMid = pts[0];
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i - 1], q = pts[i];
    const mid = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
    ctx.beginPath();
    ctx.lineWidth = (p.w + q.w) / 2;
    ctx.moveTo(prevMid.x, prevMid.y);
    ctx.quadraticCurveTo(p.x, p.y, mid.x, mid.y);
    ctx.stroke();
    prevMid = mid;
  }
  const last = pts[pts.length - 1];
  ctx.beginPath();
  ctx.lineWidth = last.w;
  ctx.moveTo(prevMid.x, prevMid.y);
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
}

function strokeBounds(pts, pad) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, wMax = 0;
  for (const p of pts) {
    if (p.x < x0) x0 = p.x; if (p.x > x1) x1 = p.x;
    if (p.y < y0) y0 = p.y; if (p.y > y1) y1 = p.y;
    if (p.w > wMax) wMax = p.w;
  }
  const m = wMax / 2 + pad;
  return { x: Math.floor(x0 - m), y: Math.floor(y0 - m), w: Math.ceil(x1 - x0 + m * 2) + 1, h: Math.ceil(y1 - y0 + m * 2) + 1, wMax };
}

let scratch = null;
function getScratch(w, h) {
  if (!scratch) scratch = document.createElement("canvas");
  if (scratch.width !== w || scratch.height !== h) { scratch.width = w; scratch.height = h; }
  return scratch;
}

// airbrush: soft round stamps along the path that build up
function drawSpray(ctx, pts, color) {
  const stamp = (x, y, w) => {
    const r = w * 0.9;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, color);
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  };
  ctx.globalAlpha = 0.14;
  stamp(pts[0].x, pts[0].y, pts[0].w);
  let carry = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    const step = Math.max(1, ((a.w + b.w) / 2) * 0.18);
    let t = step - carry;
    while (t <= d) {
      const k = t / d;
      stamp(a.x + (b.x - a.x) * k, a.y + (b.y - a.y) * k, a.w + (b.w - a.w) * k);
      t += step;
    }
    carry = d - (t - step);
  }
}

/**
 * Draw one stroke. stroke = { color, erase, brush?, points: [{x,y,w}] }
 * or a bucket fill { fill: true, canvas, x, y }.
 *   brush "pen"    crisp (default)
 *         "marker" translucent, no darkening where it overlaps itself
 *         "soft"   feathered edges
 *         "spray"  airbrush that builds up
 * The eraser is always crisp.
 */
export function drawStroke(ctx, stroke) {
  if (stroke.fill) {
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.drawImage(stroke.canvas, stroke.x, stroke.y);
    ctx.restore();
    return;
  }
  const pts = stroke.points;
  if (!pts?.length) return;
  const color = stroke.color || "#111";
  const brush = stroke.erase ? "pen" : stroke.brush || "pen";
  ctx.save();
  ctx.globalCompositeOperation = stroke.erase ? "destination-out" : "source-over";

  if (brush === "pen") {
    drawPath(ctx, pts, color);
  } else if (brush === "spray") {
    drawSpray(ctx, pts, color);
  } else {
    // marker / soft: render the whole stroke opaque on a scratch canvas,
    // then composite it once (so overlaps don't stack up)
    const soft = brush === "soft";
    const bb = strokeBounds(pts, soft ? 4 : 2);
    const blur = soft ? Math.max(2, bb.wMax * 0.45) : 0;
    const pad = Math.ceil(blur * 2);
    const W = bb.w + pad * 2, H = bb.h + pad * 2;
    const sc = getScratch(W, H);
    const sctx = sc.getContext("2d");
    sctx.clearRect(0, 0, W, H);
    sctx.save();
    sctx.translate(pad - bb.x, pad - bb.y);
    drawPath(sctx, pts, color);
    sctx.restore();
    if (soft) {
      // feather via shadow (works in every browser, unlike ctx.filter):
      // draw the shape far off-canvas and let only its blurred shadow land
      const OFF = 100000;
      ctx.shadowColor = color;
      ctx.shadowBlur = blur;
      ctx.shadowOffsetX = OFF;
      ctx.drawImage(sc, bb.x - pad - OFF, bb.y - pad);
    } else {
      ctx.globalAlpha = 0.55;
      ctx.drawImage(sc, bb.x - pad, bb.y - pad);
    }
  }
  ctx.restore();
}

export function renderStrokes(ctx, strokes) {
  for (const s of strokes) drawStroke(ctx, s);
}

/** Alpha bounding box of a canvas, or null if empty. */
export function alphaBounds(canvas, threshold = 4) {
  const { width: w, height: h } = canvas;
  const d = canvas.getContext("2d").getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w * 4;
    for (let x = 0; x < w; x++) {
      if (d[row + x * 4 + 3] > threshold) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  return x1 < 0 ? null : { x0, y0, x1: x1 + 1, y1: y1 + 1 };
}
